import { Form } from './form';
import { DonationView } from '../view/donation-view';
import { currencySymbols } from '../config/currency-symbols';
import { DonationValidator } from '../validators/donation-validator';
import { DonationProvider } from '../providers/donation-provider';
import { FormTypes } from './form-types';
import { Config } from '../config';
import { Order } from '../config/order';
import { Product } from '../config/product';
import { ProductType } from '../config/product-type';
import { DonationFrequencyType } from '../config/donation-frequency-type';

export class DonationForm extends Form {

    view: DonationView = new DonationView();
    type = FormTypes.DonationForm;
    validator: DonationValidator = new DonationValidator();
    provider: DonationProvider = new DonationProvider();
    recoveredProcessingFee = 0;
    minimumDonation = undefined;
    isBuilder = false;
    private readonly COVER_FEES_PERCENT: Number = 2.9;

    constructor(step: number | null = null) {
        super();
        this.step = step;
    }

    async init(config: Config, callbacks: any) {
        await super.init(config, callbacks);
        return Promise.resolve();
    }

    async render() {
        const currencySymbol = currencySymbols[this.config.settings.currency];
        const donation = this.config.products.find((product: Product) => product.type === ProductType.DONATION) || {};
        const donationAmount = donation.amount || 0;
        let plans: any = [];
        donation.frequency && donation.frequency.length ? plans = this.parsePlans(donation.frequency) : plans = this.provider.subscriptionPlans;
        
        this.setDefaultFrequency(donation, plans);
        this.minimumDonation = donation.minimumDonation;
        this.isBuilder = this.config.isBuilder;

        this.view.renderDonationForm(donationAmount, currencySymbol, plans,
            this.config.settings.donationFieldOnTop, donation.amounts, this.config.legacyDonationIntervals,
            this.giftAmountClickCallback.bind(this), this.intervalClickCallback.bind(this), donation.minimumDonation, this.isBuilder);

        const inputAmountElement = document.getElementById('amount') as HTMLInputElement;
        const initialAmount = +inputAmountElement.value;
        inputAmountElement.value = `${initialAmount}`;
        this.paymentData.amount = initialAmount;
        
        inputAmountElement.oninput = (event) => {
            let amount = +inputAmountElement.value.replace(',', '.');
            if (Number.isNaN(amount)) {
                amount = 0;
            }
            this.paymentData.amount = amount;
            inputAmountElement.value = `${amount}`;
            this.triggerEvent('amountChange');
            if (this.isCoverFeesEnabed()) {
                this.calculateCoverFees();
            }
            if (event !== null && event !== undefined) {
                this.view.removeActiveGiftAmountButton();
            }
        };

        inputAmountElement.addEventListener('keypress', (event: any) => {
            if (event.keyCode === 13 || event.which === 13) {
                event.preventDefault();
                return false;
            }
        });

        if (this.step) {
            this.hideForm();
        }
    }

    parsePlans(plans: any) {
        return this.provider.subscriptionPlans.filter((plan: any) => plans.includes(plan.value));
    }

    setDefaultFrequency(product: Product, plans: any[]) {
        if (plans.length === 1) {
            return;
        }
        const defaultFrequency = product.defaultFrequency;
        if (defaultFrequency) {
            const defaultPlan = plans.find(plan => plan.value === product.defaultFrequency);
            if (defaultPlan) {
                defaultPlan.selected = true;
            }
        } else {
            const monthlyPlan = plans.find(plan => plan.value === DonationFrequencyType.MONTHLY);
            monthlyPlan.selected = true;
        }
    }

    giftAmountClickCallback(event: any) {
        const inputAmount: HTMLInputElement = document.getElementById('amount') as HTMLInputElement;
        if (inputAmount) {
            const giftAmountButtonElement = event.target as HTMLInputElement;
            inputAmount.value = `${giftAmountButtonElement.getAttribute('amount')}`;
            (inputAmount as any).oninput();
            this.view.removeActiveGiftAmountButton();
            this.view.setActiveGiftAmountButton(giftAmountButtonElement);
        }
    }

    intervalClickCallback(event: any) {
        const planIntervalElement: HTMLSelectElement = this.view.getPlanIntervalElement();
        if (planIntervalElement) {
            const intervalButtonElement = event.target as HTMLInputElement;
            planIntervalElement.value = `${intervalButtonElement.getAttribute('interval')}`;
            // Trigger event for re-rendering PayPal's form.
            planIntervalElement.dispatchEvent(new CustomEvent('change', {}));
            this.view.removeActiveIntervalButton();
            this.view.setActiveIntervalButton(intervalButtonElement);
            // Trigger event for re-rendering subscription interval in total amount section.
            this.triggerEvent('amountChange');
        }
    }

    setAmountFromUrl(urlAmount: string) {
        const inputAmountElement = document.getElementById('amount') as HTMLInputElement;
        inputAmountElement.value = urlAmount;
        (inputAmountElement as any).oninput();
        this.view.removeActiveGiftAmountButton();
    }

    getDonationName() {
        if (!this.config.products) {
            return null;
        }
        const donation = this.config.products.filter((product: Product) => {
            return product.type === ProductType.DONATION;
        });
        const name = donation[0].name;
        return name;
    }

    submit(order: Order) {

        //const submissionData: any = {};
        const submissionResult = this.getSubmissionResult();

        let selectedProducts = [submissionResult];
        selectedProducts = this.setDiscount(selectedProducts);

        selectedProducts.forEach(product => {
            order.selectedProducts.push(product);
        });

        return Promise.resolve(order);
    }

    getSubmissionResult() {
        const subscriptionFrequency = this.view.getSubscriptionPlan();
        const submissionResult = {
            name: this.getDonationName(),
            type: ProductType.DONATION,
            dynamicPlan: true,
            dynamicPlanInterval: subscriptionFrequency,
            subscriptionPlanId: 'df_dynamic',
            amount: `${this.paymentData.amount}`,
            recoveredProcessingFee: this.recoveredProcessingFee
        };

        if (subscriptionFrequency === DonationFrequencyType.ONE_TIME) {
            Object.assign(submissionResult, {
                type: ProductType.DONATION,
                dynamicPlan: false,
                subscriptionPlan: undefined,
                dynamicPlanInterval: undefined,
                recoveredProcessingFee: this.recoveredProcessingFee
            });
        }
        return submissionResult;
    }

    async validate() {
        this.view.hideError();
        if (this.paymentData.amount === 0 && this.view.amountChanged) {
            this.view.showError();
            return Promise.resolve(false);
        }
        const validatorResult = this.validator.validate(
            this.paymentData.amount.toString(),
            this.view,
            this.minimumDonation,
            this.isBuilder
        );
        const validity = await validatorResult;
        return validity;
    }

    getSelectedProducts() {
        const submissionResult = this.getSubmissionResult();
        return [submissionResult];
    }

    private setDiscount(selectedProducts: Array<any>) {
        if (this.config.settings && this.config.settings.discType ) {
            const type = this.config.settings.discType;
            const coupon = this.config.settings[type];
            return selectedProducts.map((item: any) => {
                item[type] = coupon.id;
                return item;
            });
        }
        return selectedProducts;
    }

    isCoverFeesEnabed() {
        const coverFees = this.config.settings.coverFees;
        return coverFees !== null && coverFees !== undefined && coverFees === true;
    }

    calculateCoverFees() {
        const amountElement = document.getElementById('amount') as HTMLInputElement;
        const amount = +amountElement.value;
        if (amount === 0) {
            return;
        }
        if (this.getCoverFeesElement().checked) {
            const coverFeesPercent = this.getCoverFeesPercent();
            const coverFeesValue = coverFeesPercent / 100;
            this.recoveredProcessingFee = (amount * coverFeesValue + 0.3) + (amount * coverFeesValue + 0.3) * coverFeesValue;
            const finalAmount = amount + this.recoveredProcessingFee;
            this.paymentData.amount = finalAmount;
        } else {
            this.recoveredProcessingFee = 0;
            this.paymentData.amount = amount;
        }
        this.triggerEvent('amountChange');
    }

    getCoverFeesPercent() {
        const coverFeesPercent = this.config.settings.coverFeesPercent;
        return  coverFeesPercent !== null && coverFeesPercent !== undefined ?
                    coverFeesPercent :
                    this.COVER_FEES_PERCENT;

    }

    getCoverFeesElement() {
        return document.getElementById('coverFees') as HTMLInputElement;
    }

    showForm() {
        this.view.showForm();
    }

    hideForm() {
        this.view.hideForm();
    }

}
